import React, { useCallback, useEffect, useState } from 'react';
import { FiCheckCircle, FiEdit2, FiFilter, FiImage, FiPlus, FiRefreshCw, FiSearch, FiTrash2, FiX, FiXCircle } from 'react-icons/fi';
import { JewelryService } from '../services/ZashaniService';
import { ButtonSpinner, PanelLoader, TableEmptyState } from '../components/LoadingStates';

const emptyItem = {
    code: '',
    category: '',
    description: '',
    realCost: '',
    suggestedPrice: '',
    stock: 0,
    minStock: 1,
    image: null,
    catalogVisible: false,
    catalogFeatured: false,
};
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const INVENTORY_PAGE_SIZE = 50;
const inventoryViews = [
    { key: 'all', label: 'Todos' },
    { key: 'missingImage', label: 'Sin imagen' },
    { key: 'withImage', label: 'Con imagen' },
    { key: 'lowStock', label: 'Stock bajo' },
    { key: 'published', label: 'Publicados' },
];

const dataUrlBytes = (dataUrl) => {
    const base64 = String(dataUrl || '').split(',')[1] || '';
    return Math.ceil((base64.length * 3) / 4);
};

const loadImageElement = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No fue posible leer la imagen.'));
    image.src = url;
});

const canvasToDataUrl = (canvas, quality) => canvas.toDataURL('image/jpeg', quality);

const drawResizedImage = async (file, maxSize, quality) => {
    const objectUrl = URL.createObjectURL(file);
    try {
        const image = await loadImageElement(objectUrl);
        const ratio = Math.min(maxSize / image.width, maxSize / image.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * ratio));
        canvas.height = Math.max(1, Math.round(image.height * ratio));
        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        return canvasToDataUrl(canvas, quality);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

const resizeImageFile = async (file) => {
    if (!file.type.startsWith('image/')) {
        throw new Error('Selecciona un archivo de imagen valido.');
    }

    let dataUrl = await drawResizedImage(file, 1200, 0.82);
    if (dataUrlBytes(dataUrl) > MAX_IMAGE_BYTES) {
        dataUrl = await drawResizedImage(file, 900, 0.7);
    }
    if (dataUrlBytes(dataUrl) > MAX_IMAGE_BYTES) {
        throw new Error('La imagen sigue pesando mas de 2MB. Proba con una foto mas liviana.');
    }
    return dataUrl;
};

const Inventory = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(emptyItem);
    const [editSaving, setEditSaving] = useState(false);
    const [editMessage, setEditMessage] = useState('');
    const [loadingEditId, setLoadingEditId] = useState('');
    const [deletingId, setDeletingId] = useState('');
    const [productModalMode, setProductModalMode] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ search: '', view: 'all' });
    const [pagination, setPagination] = useState({ page: 1, limit: INVENTORY_PAGE_SIZE, total: 0, pages: 1 });

    const load = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: INVENTORY_PAGE_SIZE,
                search: filters.search || undefined,
            };
            if (filters.view === 'missingImage') params.image = 'missing';
            if (filters.view === 'withImage') params.image = 'with';
            if (filters.view === 'lowStock') params.lowStock = true;
            if (filters.view === 'published') params.catalogVisible = true;

            const data = await JewelryService.listPage(params);
            setItems(data.items || []);
            setPagination({
                page: data.page || page,
                limit: data.limit || INVENTORY_PAGE_SIZE,
                total: data.total || 0,
                pages: data.pages || 1,
            });
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar inventario.');
        } finally {
            setLoading(false);
        }
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const pageStats = {
        shown: items.length,
        withImage: items.filter((item) => item.image?.hasImage || item.image?.dataUrl).length,
        missingImage: items.filter((item) => !(item.image?.hasImage || item.image?.dataUrl)).length,
        lowStock: items.filter((item) => Number(item.stock || 0) <= Number(item.minStock || 0)).length,
        published: items.filter((item) => item.catalogVisible).length,
    };

    const applySearch = (event) => {
        event.preventDefault();
        setFilters((current) => ({ ...current, search: searchTerm.trim() }));
    };

    const changeView = (view) => {
        setFilters((current) => ({ ...current, view }));
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilters({ search: '', view: 'all' });
    };

    const buildPayload = (source) => ({
        ...source,
        realCost: Number(source.realCost),
        suggestedPrice: Number(source.suggestedPrice || 0),
        stock: Number(source.stock || 0),
        minStock: Number(source.minStock || 0),
        image: source.image,
    });

    const openCreateProduct = () => {
        setEditingId(null);
        setEditForm(emptyItem);
        setEditMessage('');
        setProductModalMode('create');
    };

    const editItem = async (item) => {
        setLoadingEditId(item._id);
        setEditMessage('');
        try {
            const fullItem = await JewelryService.get(item._id);
            setEditingId(fullItem._id);
            setEditForm({
                code: fullItem.code || '',
                category: fullItem.category || '',
                description: fullItem.description || '',
                realCost: fullItem.realCost || '',
                suggestedPrice: fullItem.suggestedPrice || '',
                stock: fullItem.stock || 0,
                minStock: fullItem.minStock || 1,
                image: fullItem.image?.dataUrl ? fullItem.image : null,
                catalogVisible: Boolean(fullItem.catalogVisible),
                catalogFeatured: Boolean(fullItem.catalogFeatured),
            });
            setProductModalMode('edit');
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar el producto.');
        } finally {
            setLoadingEditId('');
        }
    };

    const submitProduct = async (event) => {
        event.preventDefault();
        setEditSaving(true);
        setEditMessage('');
        try {
            if (productModalMode === 'edit') {
                await JewelryService.update(editingId, buildPayload(editForm));
            } else {
                await JewelryService.create(buildPayload(editForm));
            }
            setEditingId(null);
            setEditForm(emptyItem);
            setProductModalMode('');
            setMessage(productModalMode === 'edit' ? 'Producto actualizado.' : 'Producto guardado.');
            await load(pagination.page);
        } catch (error) {
            setEditMessage(error.response?.data?.message || 'No fue posible guardar el producto.');
        } finally {
            setEditSaving(false);
        }
    };

    const handleImageChange = async (event, setTargetForm = setEditForm, setBusy = setEditSaving, setTargetMessage = setEditMessage) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setBusy(true);
        setTargetMessage('');
        try {
            const dataUrl = await resizeImageFile(file);
            setTargetForm((current) => ({
                ...current,
                image: {
                    dataUrl,
                    mimeType: 'image/jpeg',
                    fileName: file.name,
                },
            }));
        } catch (error) {
            setTargetMessage(error.message || 'No fue posible preparar la imagen.');
        } finally {
            setBusy(false);
            event.target.value = '';
        }
    };

    const closeProductModal = () => {
        if (editSaving) return;
        setEditingId(null);
        setEditForm(emptyItem);
        setEditMessage('');
        setProductModalMode('');
    };

    const deleteItem = async (item) => {
        if (!window.confirm(`Eliminar producto ${item.code}?`)) return;
        setDeletingId(item._id);
        setMessage('');
        try {
            await JewelryService.delete(item._id);
            setMessage('Producto eliminado.');
            await load(pagination.page);
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible eliminar el producto.');
        } finally {
            setDeletingId('');
        }
    };

    const renderProductForm = ({
        values,
        setValues,
        onSubmit,
        busy,
        title,
        loadingLabel,
        submitLabel,
        busySubmitLabel,
        notice,
        onCancel,
        showCancelButton = true,
        className = 'panel',
        titleId,
    }) => (
        <form className={`${className} ${busy ? 'is-busy' : ''}`} onSubmit={onSubmit}>
            {busy && <PanelLoader label={loadingLabel} />}
            <div className="form-title-row">
                <h2 id={titleId}>{title}</h2>
                {onCancel && showCancelButton && <button className="ghost-button muted" type="button" onClick={onCancel}><FiX /><span>Cancelar</span></button>}
            </div>
            {notice && <div className="notice">{notice}</div>}
            <label>Codigo<input value={values.code} onChange={(e) => setValues({ ...values, code: e.target.value })} required /></label>
            <label>Categoria<input value={values.category} onChange={(e) => setValues({ ...values, category: e.target.value })} /></label>
            <label>Descripcion<textarea value={values.description} onChange={(e) => setValues({ ...values, description: e.target.value })} /></label>
            <div className="product-image-uploader">
                <div className="product-image-preview">
                    {values.image?.dataUrl ? (
                        <img src={values.image.dataUrl} alt={values.code ? `Producto ${values.code}` : 'Vista previa del producto'} />
                    ) : (
                        <FiImage />
                    )}
                </div>
                <label>Imagen del producto
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => handleImageChange(event, setValues, setEditSaving, setEditMessage)} />
                </label>
                {values.image?.dataUrl && (
                    <button className="ghost-button muted" type="button" onClick={() => setValues({ ...values, image: null })}>
                        <FiX />
                        <span>Quitar imagen</span>
                    </button>
                )}
            </div>
            <div className="form-grid">
                <label>Costo real<input type="number" min="0" step="0.01" value={values.realCost} onChange={(e) => setValues({ ...values, realCost: e.target.value })} required /></label>
                <label>Precio sugerido<input type="number" min="0" step="0.01" value={values.suggestedPrice} onChange={(e) => setValues({ ...values, suggestedPrice: e.target.value })} /></label>
                <label>Existencia<input type="number" min="0" value={values.stock} onChange={(e) => setValues({ ...values, stock: e.target.value })} /></label>
                <label>Minima<input type="number" min="0" value={values.minStock} onChange={(e) => setValues({ ...values, minStock: e.target.value })} /></label>
            </div>
            <div className="toggle-grid">
                <label className="switch-row">
                    <input type="checkbox" checked={values.catalogVisible} onChange={(event) => setValues({ ...values, catalogVisible: event.target.checked, catalogFeatured: event.target.checked ? values.catalogFeatured : false })} />
                    <span>Mostrar en catalogo publico</span>
                </label>
                <label className="switch-row">
                    <input type="checkbox" checked={values.catalogFeatured} disabled={!values.catalogVisible} onChange={(event) => setValues({ ...values, catalogFeatured: event.target.checked })} />
                    <span>Destacar en catalogo</span>
                </label>
            </div>
            <button className="primary-button" type="submit" disabled={busy}>
                {busy ? <ButtonSpinner /> : <FiPlus />}
                <span>{busy ? busySubmitLabel : submitLabel}</span>
            </button>
        </form>
    );

    const renderImageStatus = (item) => {
        const hasImage = Boolean(item.image?.hasImage || item.image?.dataUrl);
        return (
            <span className={`image-status ${hasImage ? 'is-ready' : 'is-missing'}`}>
                {hasImage ? <FiCheckCircle /> : <FiXCircle />}
                <span>{hasImage ? 'Con imagen' : 'Sin imagen'}</span>
            </span>
        );
    };

    return (
        <section className="content-section inventory-page">
            <div className="section-heading inventory-heading">
                <div>
                    <p className="eyebrow">Inventario</p>
                    <h2>Productos</h2>
                    <span className="table-subtitle">Vista amplia para preparar catalogo, stock y fotos.</span>
                </div>
                <button className="primary-button" type="button" onClick={openCreateProduct}>
                    <FiPlus />
                    <span>Nuevo producto</span>
                </button>
            </div>

            <div className="inventory-insights">
                <article className="inventory-stat"><span>Total filtrado</span><strong>{pagination.total}</strong></article>
                <article className="inventory-stat"><span>En esta pagina</span><strong>{pageStats.shown}</strong></article>
                <article className="inventory-stat success"><span>Con imagen</span><strong>{pageStats.withImage}</strong></article>
                <article className="inventory-stat danger"><span>Sin imagen</span><strong>{pageStats.missingImage}</strong></article>
                <article className="inventory-stat warning"><span>Stock bajo</span><strong>{pageStats.lowStock}</strong></article>
                <article className="inventory-stat"><span>Publicados</span><strong>{pageStats.published}</strong></article>
            </div>

            <div className="panel">
                <div className="table-toolbar">
                    <form className="inventory-control-bar" onSubmit={applySearch}>
                        <label className="search-control">
                            <FiSearch />
                            <input placeholder="Buscar codigo, categoria o descripcion" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
                        </label>
                        <button className="ghost-button" type="submit">
                            <FiFilter />
                            <span>Buscar</span>
                        </button>
                        <button className={`icon-button ${loading ? 'is-spinning' : ''}`} type="button" onClick={() => load(1)} disabled={loading} aria-label="Actualizar inventario">
                            <FiRefreshCw />
                        </button>
                    </form>
                    <div className="inventory-filter-chips" role="tablist" aria-label="Filtros de inventario">
                        {inventoryViews.map((view) => (
                            <button
                                key={view.key}
                                className={filters.view === view.key ? 'active' : ''}
                                type="button"
                                onClick={() => changeView(view.key)}
                            >
                                {view.label}
                            </button>
                        ))}
                        {(filters.search || filters.view !== 'all') && (
                            <button className="muted" type="button" onClick={clearFilters}>Limpiar</button>
                        )}
                    </div>
                </div>
                {message && <div className="notice">{message}</div>}
                <div className="table-wrap">
                    {loading || items.length === 0 ? (
                        <TableEmptyState loading={loading} emptyText="No hay joyas en inventario." />
                    ) : (
                        <table>
                            <thead><tr><th>Foto</th><th>Codigo</th><th>Categoria</th><th>Costo</th><th>Precio</th><th>Stock</th><th>Catalogo</th><th>Acciones</th></tr></thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item._id} className={item.stock <= item.minStock ? 'danger-row' : ''}>
                                        <td>{renderImageStatus(item)}</td>
                                        <td>{item.code}</td><td>{item.category}</td><td>Q{item.realCost}</td><td>Q{item.suggestedPrice}</td><td>{item.stock}</td>
                                        <td>{item.catalogVisible ? 'Publicado' : 'Oculto'}<small>{item.catalogFeatured ? 'Destacado' : ''}</small></td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="icon-button" type="button" onClick={() => editItem(item)} disabled={Boolean(loadingEditId) || Boolean(deletingId)} aria-label="Editar producto">
                                                    {loadingEditId === item._id ? <ButtonSpinner /> : <FiEdit2 />}
                                                </button>
                                                <button className="icon-button danger" type="button" onClick={() => deleteItem(item)} disabled={Boolean(loadingEditId) || Boolean(deletingId)} aria-label="Eliminar producto">
                                                    {deletingId === item._id ? <ButtonSpinner /> : <FiTrash2 />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="pagination-bar">
                    <button className="ghost-button muted" type="button" disabled={loading || pagination.page <= 1} onClick={() => load(pagination.page - 1)}>Anterior</button>
                    <span>Pagina {pagination.page} de {pagination.pages} · {pagination.total} productos</span>
                    <button className="ghost-button muted" type="button" disabled={loading || pagination.page >= pagination.pages} onClick={() => load(pagination.page + 1)}>Siguiente</button>
                </div>
            </div>
            {productModalMode && (
                <div className="sale-modal-backdrop" role="presentation" onClick={closeProductModal}>
                    <article className="sale-modal product-edit-modal" role="dialog" aria-modal="true" aria-labelledby="product-edit-title" onClick={(event) => event.stopPropagation()}>
                        <button className="modal-close-button" type="button" onClick={closeProductModal} aria-label="Cerrar producto">
                            <FiX />
                        </button>
                        {renderProductForm({
                            values: editForm,
                            setValues: setEditForm,
                            onSubmit: submitProduct,
                            busy: editSaving,
                            title: productModalMode === 'edit' ? 'Editar producto' : 'Nuevo producto',
                            loadingLabel: productModalMode === 'edit' ? 'Actualizando producto...' : 'Guardando producto...',
                            submitLabel: productModalMode === 'edit' ? 'Actualizar' : 'Guardar',
                            busySubmitLabel: 'Guardando...',
                            notice: editMessage,
                            onCancel: closeProductModal,
                            showCancelButton: false,
                            className: 'panel product-edit-form',
                            titleId: 'product-edit-title',
                        })}
                    </article>
                </div>
            )}
        </section>
    );
};

export default Inventory;
